import { DeleteOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Typography,
  message,
} from "antd";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ErrorC from "../components/apollo_error";
import LoadingSpinner from "../components/loading_indicator";
import {
  archive_order,
  cancel_order,
  getArchiveOrderVar,
  getCancelOrderVar,
  getOneOrderVar,
  get_one_order,
} from "../graphql/order";
import { get_user_metadata, getUserMetadataVar } from "../graphql/user";
import { formatMoney, getCurrency } from "../helpers/utils";
import withClinic from "../hooks/with_clinic";
import paths from "../routes/paths";
import {
  ApolloResult,
  Clinic,
  Order,
  OrderItem,
  OrderPayment,
  User,
} from "../store";

interface P {
  currentClinic?: Clinic;
  user?: User;
}

function SaleDetail(props: P) {
  const userId = props.user?.userId || "";
  const clinicId = props.currentClinic?.id || "";
  const navigate = useNavigate();
  const { id: orderId = "" } = useParams() as { id?: string };
  const [form] = Form.useForm();
  const [archiveModalVisible, setArchiveModalVisible] = useState(false);
  const [isSupport, setSupport] = useState(false);

  const { data, loading, error } = useQuery<ApolloResult<"getOrder", Order>>(
    get_one_order,
    {
      variables: getOneOrderVar(orderId, clinicId),
    }
  );
  const [cancelOrder, { loading: saving }] =
    useMutation<ApolloResult<"fallbackSaleOrder", Order>>(cancel_order);
  const [archiveOrder, { loading: archiving }] =
    useMutation<ApolloResult<"updateOneOrder", Pick<Order, "id">>>(
      archive_order
    );
  const { data: userMetadata } = useQuery<
    ApolloResult<"user", { id: string; metadata: string }>
  >(get_user_metadata, {
    variables: getUserMetadataVar(userId),
  });

  useEffect(() => {
    if (userMetadata) {
      setSupport(
        JSON.parse(userMetadata.user.metadata)?.allow_order_control === true
      );
    }
  }, [userMetadata]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorC error={error} />;
  if (!data || !data.getOrder) return <Empty description={"No Order"} />;

  const note = JSON.parse(data.getOrder?.metadata || "{}")?.merchant_note;

  const handleCancel = async () => {
    const variables = getCancelOrderVar({ userId, orderId });
    try {
      const result = await cancelOrder({ variables });
      if (result.data?.fallbackSaleOrder) {
        message.success("cancel success");
        navigate(paths.sales);
      } else {
        if (result.errors && result.errors.length > 0) {
          const err = result.errors[0].message;
          message.error(`failed: ${err}`);
        } else {
          message.error("failed");
        }
      }
    } catch (error) {
      let msg = "Unknown Error";
      if (error instanceof Error) msg = error.message;
      message.error(msg);
    }
  };

  const showArchiveModal = () => {
    setArchiveModalVisible(true);
  };

  const handleArchiveModalCancel = () => {
    setArchiveModalVisible(false);
    form.resetFields();
  };

  const handleArchiveModalOk = async () => {
    try {
      const values = await form.validateFields();
      const order = data.getOrder;
      const variables = getArchiveOrderVar({
        orderId,
        log: {
          log_type: "WARN",
          code: clinicId,
          log_id: `${orderId}_${new Date().getTime()}`,
          metadata: JSON.stringify({
            ...order,
            comment: values.comment,
            type: "order",
            action: "cancel",
          }),
          user: { connect: { id: userId } },
        },
      });

      const result = await archiveOrder({ variables });
      if (result.data?.updateOneOrder) {
        message.success("Archive success");
        setArchiveModalVisible(false);
        navigate(paths.sales);
      } else {
        if (result.errors && result.errors.length > 0) {
          const err = result.errors[0].message;
          message.error(`failed: ${err}`);
        } else {
          message.error("failed");
        }
      }
    } catch (error) {
      let msg = "Unknown Error";
      if (error instanceof Error) msg = error.message;
      message.error(msg);
    }
  };

  const getMemberName = () => {
    const name =
      data.getOrder.member.clinic_members.length > 0
        ? data.getOrder.member.clinic_members[0].name
        : data.getOrder.member.name;
    return name;
  };

  return (
    <>
      <Card
        size="small"
        type="inner"
        title={
          <p>
            OrderNo: {data.getOrder.order_id} | Date:{" "}
            {format(new Date(data.getOrder.created_at), "dd MMM,yyyy")} |{" "}
            {data.getOrder.status} | {data.getOrder.payment_method} |{" "}
            <Link to={paths.getMemberDetailRoute(data.getOrder.member_id)}>
              {getMemberName()}
            </Link>
          </p>
        }
        actions={[
          <List size="small">
            {data.getOrder.seller != null &&
              data.getOrder.seller.display_name != null &&
              data.getOrder.seller.display_name !== "" && (
                <List.Item
                  actions={[
                    <Typography style={{ width: 200, textAlign: "right" }}>
                      {data.getOrder.seller.display_name}
                    </Typography>,
                  ]}
                >
                  <List.Item.Meta />
                  <Typography.Paragraph
                    style={{ minWidth: 100, textAlign: "left" }}
                  >
                    Seller
                  </Typography.Paragraph>
                </List.Item>
              )}
            <List.Item
              actions={[
                <Typography style={{ width: 200, textAlign: "right" }}>
                  {formatMoney(Number(data.getOrder.total))}{" "}
                  {getCurrency(props.currentClinic)}
                </Typography>,
              ]}
            >
              <List.Item.Meta />
              <Typography.Paragraph
                style={{ minWidth: 100, textAlign: "left" }}
              >
                Subtotal
              </Typography.Paragraph>
            </List.Item>
            <List.Item
              actions={[
                <Typography style={{ width: 200, textAlign: "right" }}>
                  {Number(data.getOrder.tax) > 0
                    ? `${formatMoney(Number(data.getOrder.tax))} ${getCurrency(
                        props.currentClinic
                      )}`
                    : "-"}
                </Typography>,
              ]}
            >
              <List.Item.Meta />
              <Typography.Paragraph
                style={{ minWidth: 100, textAlign: "left" }}
              >
                Tax
              </Typography.Paragraph>
            </List.Item>
            <List.Item
              actions={[
                <Typography style={{ width: 200, textAlign: "right" }}>
                  {Number(data.getOrder.discount) > 0
                    ? `${formatMoney(
                        Number(data.getOrder.discount)
                      )} ${getCurrency(props.currentClinic)}`
                    : "-"}
                </Typography>,
              ]}
            >
              <List.Item.Meta />
              <Typography.Paragraph
                style={{ minWidth: 100, textAlign: "left" }}
              >
                Discount
              </Typography.Paragraph>
            </List.Item>
            <List.Item
              actions={[
                <Typography
                  style={{
                    width: 200,
                    textAlign: "right",
                  }}
                >
                  {`${formatMoney(Number(data.getOrder.net_total))} ${getCurrency(
                    props.currentClinic
                  )}`}
                </Typography>,
              ]}
            >
              <List.Item.Meta />
              <Typography.Paragraph
                style={{ minWidth: 100, textAlign: "left" }}
              >
                Total Amount
              </Typography.Paragraph>
            </List.Item>
            {data.getOrder.payment_method === "SPLIT" &&
              data.getOrder?.payments != null &&
              data.getOrder.payments.map((payment: OrderPayment, index: number) => {
                return (
                  <List.Item
                    key={`payment-${index}`}
                    actions={[
                      <Typography
                        style={{
                          width: 200,
                          textAlign: "right",
                        }}
                      >
                        {`${formatMoney(Number(payment.payment_amount))} ${getCurrency(
                          props.currentClinic
                        )}`}
                      </Typography>,
                    ]}
                  >
                    <List.Item.Meta />
                    <Typography.Paragraph
                      style={{ minWidth: 100, textAlign: "left" }}
                    >
                      {payment.payment_method} Payment
                    </Typography.Paragraph>
                  </List.Item>
                );
              })}
          </List>,
        ]}
      >
        <List
          size="small"
          className="demo-loadmore-list"
          loading={false}
          itemLayout="horizontal"
          dataSource={data.getOrder.order_items}
          renderItem={(item: OrderItem) => (
            <List.Item
              actions={[
                <Typography>
                  {formatMoney(Number(item.total))}{" "}
                  {getCurrency(props.currentClinic)}
                </Typography>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Badge count={item.quantity} color="blue">
                    <Avatar
                      src={item.service?.image || item.service_package?.image || ""}
                    />
                  </Badge>
                }
                title={
                  <Typography.Title level={5}>
                    {JSON.parse(item?.metadata || "{}")?.name ||
                      item.service?.name ||
                      item.service_package?.name ||
                      item.product_stock_item?.name ||
                      ""}
                  </Typography.Title>
                }
                description={
                  <Typography.Paragraph>
                    {item.service ? (
                      <Link to={paths.getServiceDetailRoute(item.service_id!)}>
                        service
                      </Link>
                    ) : item.service_package_id ? (
                      "service package"
                    ) : (
                      "product"
                    )}
                  </Typography.Paragraph>
                }
              />
              <Typography.Paragraph
                style={{ minWidth: 250, textAlign: "left" }}
              >
                {formatMoney(Number(item.price))} x {item.quantity}
              </Typography.Paragraph>
            </List.Item>
          )}
        />
      </Card>

      {note != null && note !== "" && (
        <div
          style={{
            width: "80%",
            marginTop: "24px",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              backgroundColor: "#fafafa",
              padding: "20px",
              border: "1px solid #E5E7EB",
            }}
          >
            <Typography.Title
              level={5}
              style={{
                marginTop: 0,
                marginBottom: "12px",
                color: "#1F2937",
                textAlign: "left",
              }}
            >
              Note
            </Typography.Title>
            <Typography.Paragraph
              style={{
                marginBottom: 0,
                lineHeight: "1.6",
                color: "#374151",
                textAlign: "left",
              }}
            >
              {note}
            </Typography.Paragraph>
          </div>
        </div>
      )}

      {data.getOrder.status !== "CANCEL" && isSupport && (
        <div style={{ marginTop: 24, textAlign: "center" }}>
          {data.getOrder.order_id.startsWith("CO-") ? (
            <Button
              loading={archiving}
              icon={<DeleteOutlined />}
              danger
              onClick={showArchiveModal}
            >
              Archive
            </Button>
          ) : (
            Number(data.getOrder.total) > 0 && (
              <Popconfirm
                placement="topRight"
                title={`Sure to cancel ${data.getOrder.order_id} ?`}
                onConfirm={handleCancel}
              >
                <Button loading={saving} icon={<DeleteOutlined />} danger>
                  Cancel
                </Button>
              </Popconfirm>
            )
          )}
        </div>
      )}

      <Modal
        title={`Archive Order ${data.getOrder.order_id}`}
        open={archiveModalVisible}
        onOk={handleArchiveModalOk}
        onCancel={handleArchiveModalCancel}
        confirmLoading={archiving}
        okText="Confirm Archive"
        cancelText="Cancel"
      >
        <Typography.Text type="danger">
          This will mark the order as cancelled, but any used items will not be
          added back.
        </Typography.Text>
        <Form form={form}>
          <Form.Item
            name="comment"
            label="Comment"
            rules={[
              {
                required: true,
                message: "Please provide a comment before archiving",
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Please explain why you are archiving this order (required)"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default withClinic(SaleDetail);
